import React from 'react';
import { Calendar, Clock, CheckCircle, XCircle, MapPin, Plus, Wrench } from 'lucide-react';
import { Visit } from '@/lib/portal-api-client';

interface VisitsTimelineSectionProps {
  upcomingVisits: Visit[];
  pastVisits: Visit[];
  onBookVisit: () => void;
}

export function VisitsTimelineSection({ 
  upcomingVisits, 
  pastVisits,
  onBookVisit 
}: VisitsTimelineSectionProps) {
  const visitTypeConfig = {
    pm: { label: 'PM Visit', color: 'text-blue-600', bgColor: 'bg-blue-100' },
    repair: { label: 'Repair', color: 'text-orange-600', bgColor: 'bg-orange-100' },
    emergency: { label: 'Emergency', color: 'text-red-600', bgColor: 'bg-red-100' },
    other: { label: 'Other', color: 'text-gray-600', bgColor: 'bg-gray-100' },
  };

  const statusConfig = {
    scheduled: { icon: Calendar, color: 'text-blue-600', label: 'Scheduled' },
    in_progress: { icon: Wrench, color: 'text-yellow-600', label: 'In Progress' },
    completed: { icon: CheckCircle, color: 'text-green-600', label: 'Completed' },
    cancelled: { icon: XCircle, color: 'text-red-600', label: 'Cancelled' },
    no_show: { icon: XCircle, color: 'text-gray-600', label: 'No Show' },
  };

  return (
    <div className="space-y-6">
      {/* Upcoming Visits */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Upcoming Visits</h2>
                <p className="text-sm text-gray-600">
                  {upcomingVisits.length} scheduled visit{upcomingVisits.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <button
              onClick={onBookVisit}
              className="py-2 px-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Book Visit
            </button>
          </div>
        </div>

        <div className="p-6">
          {upcomingVisits.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">No upcoming visits scheduled</p>
              <p className="text-sm text-gray-500 mt-1 mb-4">
                Book a visit to schedule your next service
              </p>
              <button
                onClick={onBookVisit}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Schedule Now
              </button>
            </div>
          ) : (
            <div className="grid gap-4">
              {upcomingVisits.map((visit) => {
                const visitType = visitTypeConfig[visit.visit_type] || visitTypeConfig.other;
                const status = statusConfig[visit.status] || statusConfig.scheduled;
                const StatusIcon = status.icon;

                return (
                  <div
                    key={visit.id}
                    className="border-2 border-gray-200 rounded-xl p-4 hover:border-blue-300 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${visitType.bgColor} ${visitType.color}`}>
                            {visitType.label}
                          </span>
                          <span className={`flex items-center gap-1 text-sm font-medium ${status.color}`}>
                            <StatusIcon className="w-4 h-4" />
                            {status.label}
                          </span>
                        </div>

                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2 text-gray-700">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span className="font-semibold">
                              {new Date(visit.visit_date).toLocaleDateString('en-US', {
                                weekday: 'long',
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </span>
                          </div>

                          {visit.visit_window && (
                            <div className="flex items-center gap-2 text-gray-600 text-sm">
                              <Clock className="w-4 h-4 text-gray-400" />
                              <span>{visit.visit_window}</span>
                            </div>
                          )}

                          {visit.technician_name && (
                            <div className="flex items-center gap-2 text-gray-600 text-sm">
                              <MapPin className="w-4 h-4 text-gray-400" />
                              <span>Technician: {visit.technician_name}</span>
                            </div>
                          )}

                          {(visit.job_number || visit.successware_job_number) && (
                            <div className="text-xs text-gray-500 font-mono mt-2">
                              Job #{visit.job_number || visit.successware_job_number}
                            </div>
                          )}

                          {visit.notes && (
                            <div className="mt-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-2">
                              {visit.notes}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Past Visits */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Visit History</h2>
              <p className="text-sm text-gray-600">
                {pastVisits.length} past visit{pastVisits.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>

        {pastVisits.length === 0 ? (
          <div className="p-12 text-center">
            <CheckCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">No visit history yet</p>
            <p className="text-sm text-gray-500 mt-1">Completed visits will appear here</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                    Technician
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                    Job #
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pastVisits.map((visit) => {
                  const visitType = visitTypeConfig[visit.visit_type] || visitTypeConfig.other;
                  const status = statusConfig[visit.status] || statusConfig.completed;
                  const StatusIcon = status.icon;

                  return (
                    <tr key={visit.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {new Date(visit.visit_date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${visitType.bgColor} ${visitType.color}`}>
                          {visitType.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {visit.technician_name || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`flex items-center gap-1 text-sm font-medium ${status.color}`}>
                          <StatusIcon className="w-4 h-4" />
                          {status.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm font-mono text-gray-600">
                        {visit.job_number || visit.successware_job_number || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
